import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  Stack,
  Button,
  CircularProgress,
  FormControlLabel,
  Divider,
  Alert,
} from "@mui/material";
import PinInput from "react-pin-input";
import { useSnackbar } from "notistack";
import { Icon } from "@iconify/react";

import Page from "src/components/Page";
import {
  resendTwoFactorVerification,
  twoFactorVerification,
  toggleTwoFactorApi,
} from "src/DAL/AUTH";

export default function AccountTwoFactorAuth() {
  const user = JSON.parse(localStorage.getItem("user_data"));
  const { enqueueSnackbar } = useSnackbar();
  const [enabled, setEnabled] = useState(
    localStorage.getItem("is_2f_enabled") === "true"
  );
  const [showVerification, setShowVerification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleResendCode = async () => {
    if (!canResend) return;
    const post_data = {
      detail_access_token: user?.detail_access_token,
    };

    const result = await resendTwoFactorVerification(post_data);
    if (result.code === 200) {
      enqueueSnackbar(result.message, { variant: "success" });
      setTimer(60);
      setCanResend(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleToggle2FA = async (value) => {
    setLoading(true);

    const post_data = {
      is_2f_enabled: value,
    };

    const result = await toggleTwoFactorApi(post_data);

    if (result.code === 200) {
      setEnabled(value);
      localStorage.setItem("is_2f_enabled", String(value));

      if (value) {
        setShowVerification(false);
        enqueueSnackbar(result.message, {
          variant: "success",
        });
      } else {
        setShowVerification(false);
        setVerificationCode("");
        enqueueSnackbar(result.message, {
          variant: "success",
        });
      }
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
    setLoading(false);
  };

  const handleToggleOnAndOff = async (event) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      setLoading(true);
      const post_data = {
        detail_access_token: user?.detail_access_token,
      };

      const result = await resendTwoFactorVerification(post_data);
      if (result.code === 200) {
        enqueueSnackbar("Verification code has been sent to your email", {
          variant: "success",
        });
        setShowVerification(true);
        setTimer(60);
        setCanResend(false);
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
      }
      setLoading(false);
    } else {
      await handleToggle2FA(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    const post_data = {
      verification_code: verificationCode,
      detail_access_token: user?.detail_access_token,
    };

    const result = await twoFactorVerification(post_data);
    if (result.code === 200) {
      await handleToggle2FA(true);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
    setLoading(false);
  };

  return (
    <Page title="Two-Factor Authentication">
      <Box sx={{ mx: "auto", px: 3, py: 4 }}>
        <Typography variant="h4" sx={{ mb: 5, color: "primary.main" }}>
          Two Factor Authentication
        </Typography>

        <Card
          sx={{
            boxShadow: 3,
            borderRadius: 2,
            overflow: "visible",
            backgroundColor: "#FBFBFC",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      bgcolor: enabled ? "success.lighter" : "grey.200",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: enabled ? "success.main" : "text.secondary",
                    }}
                  >
                    <Icon
                      icon={enabled ? "eva:shield-fill" : "eva:shield-off-fill"}
                      width={28}
                      height={28}
                      style={{ color: enabled ? "#00AB55" : "inherit" }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="h6">
                      {enabled
                        ? "Two-Factor Authentication is Enabled"
                        : "Enable Two-Factor Authentication"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {enabled
                        ? "Your account is secure. You will be asked for a code when logging in."
                        : "Add an extra layer of security to your account."}
                    </Typography>
                  </Box>
                </Box>

                {!showVerification && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabled}
                        onChange={handleToggleOnAndOff}
                        disabled={loading}
                        color="primary"
                      />
                    }
                    label={enabled ? "On" : "Off"}
                    labelPlacement="start"
                  />
                )}
              </Box>

              {showVerification && !enabled && (
                <>
                  <Divider />
                  <Box sx={{ py: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Verify your account
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      We have sent a verification code to your email. Please
                      enter it below to enable 2FA.
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <PinInput
                        length={6}
                        initialValue=""
                        secret={false}
                        onChange={(value) => setVerificationCode(value)}
                        type="numeric"
                        inputMode="number"
                        style={{ padding: "10px" }}
                        inputStyle={{
                          borderColor: "rgba(145, 158, 171, 0.32)",
                          borderRadius: "8px",
                          width: "70px",
                          height: "70px",
                          fontSize: "20px",
                          margin: "0 6px",
                        }}
                        inputFocusStyle={{
                          borderColor: "#EC8945",
                        }}
                        onComplete={(value) => setVerificationCode(value)}
                        autoSelect={true}
                        regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
                      />
                    </Box>

                    <Box
                      sx={{ display: "flex", justifyContent: "start", mb: 3 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Didn't receive code?{" "}
                        <Box
                          component="span"
                          sx={{
                            color: canResend ? "primary.main" : "text.disabled",
                            cursor: canResend ? "pointer" : "default",
                            fontWeight: "bold",
                            "&:hover": {
                              textDecoration: canResend ? "underline" : "none",
                            },
                          }}
                          onClick={handleResendCode}
                        >
                          {canResend ? "Resend Code" : `Resend in ${timer}s`}
                        </Box>
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                          setShowVerification(false);
                          setVerificationCode("");
                        }}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleVerify}
                        disabled={loading || verificationCode.length < 6}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Verify & Enable"
                        )}
                      </Button>
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Page>
  );
}
